import './AdministratorScreen.scss';
import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Grid, Box, Paper, Table, TableBody, TableCell, TableHead, TableRow, useMediaQuery, } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { Helmet } from 'react-helmet';
import { fetchClientsForManager, fetchTiers, updateClient } from 'src/module/admin/action';
import { getClientsForManager } from 'src/module/admin/selector';
import { Map } from 'immutable';
import moment from 'moment';
import variables from 'src/style/variable/variables.module.scss';
import ClientDetails from 'src/component/ClientDetails';
import Button from 'src/component/UI/Button';
import PermissionFence from 'src/component/PermissionFence';
import { useTranslation } from 'src/i18n';
import { setSettingsModalOpen } from 'src/module/device/action';
import { SettingsModalTypes } from 'src/component/UI/SettingsModal/SettingsModalTypes';
import AddFacilityModal from 'src/component/UI/AddFacilityModal';

export default function DashboardWithoutSidebar () {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const [detailsOpen, setDetailsOpen] = useState(false);
  const isMobile = useMediaQuery(`(max-width: ${variables.mobileWidth})`);
  const [selectedRowIndex, setSelectedRowIndex] = useState(null);  // Initialize to null
  const clients = useSelector(getClientsForManager);

  useEffect(() => {
    dispatch(fetchClientsForManager());
    dispatch(fetchTiers());
  }, [dispatch]);

  const data = clients?.toJS() || [];
  const selectedRow = selectedRowIndex !== null ? data[selectedRowIndex] : {};

  const handleRowClick = (index) => {
    setSelectedRowIndex(index);
    if (!detailsOpen) setDetailsOpen(true);
  };

  const handleDetailsClosed = () => {
    setSelectedRowIndex(null);
    setDetailsOpen(false);
  };

  const handleCellChange = (rec) => {
    dispatch(updateClient(selectedRow.id, Map(rec)));
  };

  const setReduxAddFacilityModal = useCallback(() => {
    dispatch(setSettingsModalOpen(null, SettingsModalTypes.ADD_FACILITY));
  }, [dispatch]);


  return (
    <Box sx={{ flexGrow: 1 }} id='admin-screen'>
      <Helmet>
        <title>{t('admin.manageClients')}</title>
      </Helmet>
      <Grid container spacing={2} sx={{ p: 3 }} direction={isMobile ? 'column' : 'row'}>
        {/* Table Area */}
        <Grid item xs={12} md={detailsOpen ? 8 : 12}>
          <Paper elevation={2} sx={{ padding: 2 }}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>{t('admin.clientName')}</TableCell>
                  <TableCell>{t('common.type')}</TableCell>
                  <TableCell>{t('admin.expiry')}</TableCell>
                  <TableCell>{t('common.status')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {data.map((row, index) => (
                  <TableRow
                    key={index}
                    onClick={() => handleRowClick(index)}
                    className={selectedRowIndex === index ? '' : 'highlightable'}
                    sx={{
                      cursor: 'pointer',
                      backgroundColor: selectedRowIndex === index ? 'lightgray' : 'transparent'
                    }}
                  >
                    <TableCell>{row.name}</TableCell>
                    <TableCell>{t(row.type)}</TableCell>
                    <TableCell>{row.subscription_expiry ? moment(row.subscription_expiry).format('YYYY-MM-DD') : t('common.never')}</TableCell>
                    <TableCell>{row.subscription_status === 'active' ? t('common.active') : t('common.inactive')}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
              <PermissionFence can='create-facility'>
                <Button
                  onClick={setReduxAddFacilityModal}
                  variant='contained'
                  cta
                  className='addNewFacilityButton'
                  icon={<AddIcon style={{ fontSize: 14 }} />}
                >
                  {t('admin.addFacility')}
                </Button>
              </PermissionFence>
            </Box>
          </Paper>
        </Grid>

        {/* Details Panel */}
        {detailsOpen && selectedRowIndex !== null && (
          <ClientDetails client={selectedRow} handleCellChange={handleCellChange} handleDetailsClosed={handleDetailsClosed} showGoToClient allowClose />
        )}
      </Grid>
      <PermissionFence can='create-facility'>
        <AddFacilityModal />
      </PermissionFence>
    </Box>
  );
}
